/**
 * Horizontal selected-items strip — quick review and one-tap remove.
 * Chips represent user-visible items (sub-categories); removing clears all inventory ids in the group.
 */
import React, { memo, useMemo } from 'react';
import { View, Text, StyleSheet, FlatList, Pressable } from 'react-native';
import { SvgIcon } from '../../components/common/SvgIcon';
import { useTranslation } from '../../shared/i18n';
import { appTheme, type ThemeColors } from '../../shared/theme';
import { studioSectionCardStyle, androidScaleFont } from '../../shared/theme/studio/stackScreenShell';
import type { SelectedSubCategoryChip } from '../../data/domains/preferences';

type Props = {
  colors: ThemeColors;
  items: SelectedSubCategoryChip[];
  onRemove: (item_ids: number[]) => void;
};

function createStyles(c: ThemeColors) {
  const sf = androidScaleFont;

  return StyleSheet.create({
    wrap: {
      marginBottom: appTheme.spacing.md,
    },
    header_row: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: appTheme.spacing.sm,
    },
    title: {
      ...appTheme.typography.caption,
      fontSize: sf(appTheme.typography.caption.fontSize ?? 12),
      fontWeight: '700',
      letterSpacing: 0.4,
      textTransform: 'uppercase',
      color: c.textSecondary,
    },
    count: {
      ...appTheme.typography.caption,
      fontSize: sf(appTheme.typography.caption.fontSize ?? 12),
      fontWeight: '700',
      color: c.primary,
    },
    chip: {
      flexDirection: 'row',
      alignItems: 'center',
      maxWidth: 220,
      paddingStart: appTheme.spacing.sm,
      paddingEnd: appTheme.spacing.xs,
      paddingVertical: 7,
      borderRadius: 999,
      backgroundColor: c.primaryUltra ?? `${c.primary}14`,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: c.primary,
      marginEnd: appTheme.spacing.xs,
    },
    chip_label: {
      ...appTheme.typography.caption,
      fontSize: sf(appTheme.typography.caption.fontSize ?? 12),
      fontWeight: '600',
      color: c.primary,
      flexShrink: 1,
      marginEnd: 4,
    },
    chip_remove: {
      width: 22,
      height: 22,
      borderRadius: 11,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: `${c.primary}20`,
    },
  });
}

function PreferencesSelectedChipsInner({ colors, items, onRemove }: Props) {
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const card_style = useMemo(
    () => studioSectionCardStyle(colors, { padding: appTheme.spacing.md, marginBottom: 0 }),
    [colors],
  );

  if (items.length === 0) {
    return null;
  }

  return (
    <View style={[card_style, styles.wrap]}>
      <View style={styles.header_row}>
        <Text style={styles.title}>{t('account.selected')}</Text>
      </View>
      <FlatList
        horizontal
        data={items}
        keyExtractor={item => item.key}
        showsHorizontalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        renderItem={({ item }) => (
          <View style={styles.chip}>
            <Text style={styles.chip_label} numberOfLines={1}>
              {item.title}
            </Text>
            <Pressable
              onPress={() => onRemove(item.item_ids)}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel={item.title}
              style={({ pressed }) => [styles.chip_remove, pressed && { opacity: 0.7 }]}>
              <SvgIcon name="x" size={12} color={colors.primary} />
            </Pressable>
          </View>
        )}
      />
    </View>
  );
}

export const PreferencesSelectedChips = memo(PreferencesSelectedChipsInner);
